import _has from "lodash/has";
import _set from "lodash/set";
import * as schemas from "../schemas";
import { actionPrefix } from "./constants";
import { asyncHttpAction } from "./asyncHttpAction";

export const apiPrefix = `${actionPrefix}/api`;

export function getApiState(state) {
    return state.api;
}

export const api = {
    audiences: {
        list: asyncHttpAction(
            `${apiPrefix}/audiences/GET`,
            getState => `${getState().app.apiUrl}/audiences/`,
            {
                schema: schemas.audienceSchema
            }
        )
    },
    teams: {
        wevos: {
            list: asyncHttpAction(
                `${apiPrefix}/teams/wevos/LIST`,
                (getState, teamId) =>
                    `${getState().app.apiUrl}/teams/${teamId}/wevos`,
                {
                    processResponse: ({ wevos }) => wevos,
                    schema: [schemas.wevoSchema]
                }
            )
        }
    },
    wevos: {
        get: asyncHttpAction(
            `${apiPrefix}/wevos/GET`,
            (getState, wevoId) => `${getState().app.apiUrl}/wevos/${wevoId}`,
            {
                schema: schemas.wevoSchema
            }
        ),
        updateArchived: asyncHttpAction(
            `${apiPrefix}/wevos/UPDATE_ARCHIVED`,
            (getState, wevoId) => `${getState().app.apiUrl}/wevos/${wevoId}`,
            {
                request: (getState, wevoId, isArchived) => {
                    return {
                        method: "POST",
                        body: { isArchived }
                    };
                },
                schema: schemas.wevoSchema
            }
        ),
        delete: asyncHttpAction(
            `${apiPrefix}/wevos/DELETE`,
            (getState, wevoId) => `${getState().app.apiUrl}/wevos/${wevoId}`,
            {
                request: () => {
                    return {
                        method: "DELETE"
                    };
                },
                schema: schemas.wevoSchema
            }
        ),
        results: asyncHttpAction(
            `${apiPrefix}/wevos/results/GET`,
            (getState, wevoId) =>
                `${getState().app.apiUrl}/wevos/${wevoId}/results`,
            {
                schema: schemas.wevoResultsSchema
            }
        ),
        pages: {
            results: asyncHttpAction(
                `${apiPrefix}/pages/GET_RESULTS`,
                (getState, pageId, wevoId) =>
                    `${
                        getState().app.apiUrl
                    }/wevos/${wevoId}/pages/${pageId}/results?version=v2`,
                {
                    schema: schemas.pageResultsSchema
                }
            )
        }
    },
    wevoShareLinks: {
        audiences: {
            list: asyncHttpAction(
                `${apiPrefix}/wevoShareLinks/audiences/GET`,
                (getState, shareLinkId) =>
                    `${
                        getState().app.apiUrl
                    }/wevoShareLinks/${shareLinkId}/audiences/`,
                {
                    schema: schemas.audienceSchema
                }
            )
        },
        get: asyncHttpAction(
            `${apiPrefix}/wevoShareLinks/GET`,
            (getState, shareLinkId) =>
                `${getState().app.apiUrl}/wevoShareLinks/${shareLinkId}`,
            {
                schema: schemas.wevoSchema
            }
        ),
        results: asyncHttpAction(
            `${apiPrefix}/wevos/wevoShareLinks/GET_RESULTS`,
            (getState, shareLinkId) =>
                `${
                    getState().app.apiUrl
                }/wevoShareLinks/${shareLinkId}/results`,
            {
                schema: schemas.wevoResultsSchema
            }
        ),
        pages: {
            results: asyncHttpAction(
                `${apiPrefix}/wevos/wevoShareLinks/pages/GET_RESULTS`,
                (getState, pageId, shareLinkId) =>
                    `${
                        getState().app.apiUrl
                    }/wevoShareLinks/${shareLinkId}/pages/${pageId}/results`,
                {
                    schema: schemas.pageResultsSchema
                }
            )
        }
    }
};

// The initial state has an empty object for each schema entity. The keys are the names of the schema entities (i.e.,
// the 1st argument to the schema.Entity constructor). It will look something like:
// ```
// {
//     wevos: {},
//     pages: {},
//     images: {},
//     users: {},
//     etc: {}
// }
// ```
const INITIAL_STATE = Object.values(schemas).reduce(
    (acc, entity) => _set(acc, entity.key, {}),
    {}
);

export function reducer(state = INITIAL_STATE, action) {
    // This reducer handles successful responses from HTTP requests. Instead of switching on every possible success
    // action, we use the meta.isApi field to identify HTTP requests. If the request was successful, it will have
    // entities in its payload.
    if (
        !_has(action, ["meta", "isApi"]) ||
        !_has(action, ["payload", "data", "entities"])
    ) {
        return state;
    }

    // entities looks sort of like INITIAL_STATE except that it only has keys for the entities that were returned in the
    // HTTP request
    const { entities } = action.payload.data;

    const nextState = { ...state };

    // For each entity type (e.g., "wevos") returned by the request, make a shallow copy and merge the new entity
    // objects into it.
    for (const entityType of Object.keys(entities)) {
        nextState[entityType] = { ...state[entityType] };
        for (const id of Object.keys(entities[entityType])) {
            nextState[entityType][id] = entities[entityType][id];
        }
    }

    return nextState;
}



// WEBPACK FOOTER //
// ./src/modules/api.js